import React, { useState, useRef, useEffect } from "react";
import clsx from "clsx";
import { DayPicker } from "react-day-picker";
import { ptBR } from "react-day-picker/locale";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";
import { Line } from "react-chartjs-2";
import { toast } from "react-toastify";

import styles from "../../customDashboard.module.css";
import HeaderTitle from "../../../../../components/header/headerTitle/headerTitle";
import Button from "../../../../../components/button/button";
import FilterSelect from "../../../../../components/predictive/filterSelect/filterSelect";
import useOutsideAlerter from "../../../../../utils/clickOutside";
import { formatDate } from "../../../../../utils/formatter";
import { useInstitution } from "../../../../../context/InstitutionContext";
import { 
  readAvailableIndicators, 
  previewIndicator, 
  createCustomizerDashboard,
  getDashboardData 
} from "../../../../../services/customDashboard";
import { insightsFilters } from "../../../../../services/insights";
import { readByInstitution } from "../../../../../services/academetrics";
import { saveDateRange } from "../../../../../utils/localStorage";

// Icons
import DashboardIcon from "../../../../../assets/icons/dashboard-icon.svg";
import BookIcon from "../../../../../assets/icons/book.svg";
import CourseIcon from "../../../../../assets/icons/course.svg";
import LocaleIcon from "../../../../../assets/icons/locale.svg";
import ClassesIcon from "../../../../../assets/icons/classes.svg";
import DisciplineIcon from "../../../../../assets/icons/discipline.svg";
import CalendarIcon from "../../../../../assets/icons/calendar.svg";
import MagnifyingGlassIcon from "../../../../../assets/icons/magnifying-glass-blue.svg";
import ChartMixedPorcentageIcon from "../../../../../assets/icons/chart-mixed-porcentage.svg";
import ChartMixedUpCircleDollarIcon from "../../../../../assets/icons/chart-mixed-up-circle-dollar.svg";
import ChartMixedDownCircleDollarIcon from "../../../../../assets/icons/chart-mixed-down-circle-dollar.svg";
import ChartMoneyIcon from "../../../../../assets/icons/chart-money.svg";
import FinanceIcon from "../../../../../assets/icons/finance.svg";
import AcademicIcon from "../../../../../assets/icons/academic.svg";
import ArrowLeftIcon from "../../../../../assets/icons/arrow-left.svg";
import UserIcon from "../../../../../assets/icons/user.svg";
import UsersIcon from "../../../../../assets/icons/users.svg";
import UsersTwoIcon from "../../../../../assets/icons/users-two.svg";

// Mapeamento de ícones
const ICON_MAP = {
  "chart-mixed-porcentage": ChartMixedPorcentageIcon,
  "chart-mixed-up-circle-dollar": ChartMixedUpCircleDollarIcon,
  "chart-mixed-down-circle-dollar": ChartMixedDownCircleDollarIcon,
  "chart-money": ChartMoneyIcon,
  "finance": FinanceIcon,
  "academic": AcademicIcon,
  "dashboard-icon": DashboardIcon,
  "book": BookIcon,
  "course": CourseIcon,
  "locale": LocaleIcon,
  "classes": ClassesIcon,
  "discipline": DisciplineIcon
};

const getIconByName = (iconName) => {
  return ICON_MAP[iconName] || ChartMixedDownCircleDollarIcon;
};

// Função para formatar valores baseado no labelType
const formatValueByType = (value, labelType, options = {}) => {
  const numValue = Number(value) || 0;
  
  if (labelType === "percent") {
    return new Intl.NumberFormat('pt-BR', {
      style: 'percent',
      minimumFractionDigits: options.minimumFractionDigits ?? 0,
      maximumFractionDigits: options.maximumFractionDigits ?? 2,
      ...options
    }).format(numValue / 100);
  }
  
  // Default: currency
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    ...options
  }).format(numValue);
};


// TODO: Remover quando o backend retornar dados reais para estes indicadores.
// Valores mockados temporários exibidos quando o indicador estiver presente em um dashboard.
const MOCK_INDICATOR_VALUES = [
  {
    matcher: /mensalidades?\s+inadimplent/i,
    value: 33,
    labelType: "percent"
  },
  {
    matcher: /valor\s+inadimplent/i,
    value: 1254.24,
    labelType: "currency"
  },
  {
    matcher: /taxa\s+de\s+inadimpl[eê]ncia/i,
    value: 1350.23,
    labelType: "currency"
  }
];

const getMockedValueForIndicator = (indicator) => {
  if (!indicator?.name) return null;
  return MOCK_INDICATOR_VALUES.find((mock) => mock.matcher.test(indicator.name)) || null;
};

const applyMockedValueToPreview = (previewData, indicator) => {
  const mock = getMockedValueForIndicator(indicator);
  if (!mock) return previewData;

  const base = previewData || {
    name: indicator.name,
    icon: indicator.icon,
    category: indicator.category
  };

  return {
    ...base,
    visualizationType: "label",
    labelType: mock.labelType,
    data: {
      ...(base.data || {}),
      value: mock.value
    }
  };
};

// Indicadores que devem ocupar a linha inteira do grid quando adicionados ao dashboard.
const FULL_ROW_INDICATOR_PATTERNS = [
  /desconto\s+m[eé]dio\s+sobre\s+o\s+faturado/i
];

const shouldOccupyFullRow = (indicator) => {
  if (!indicator?.name) return false;
  return FULL_ROW_INDICATOR_PATTERNS.some((pattern) => pattern.test(indicator.name));
};

const GRID_COLUMNS = 4;
const DEFAULT_BOX_WIDTH = 280;
const DEFAULT_BOX_HEIGHT = 280;

const INITIAL_BOX_SIZES = {
  box1: { width: DEFAULT_BOX_WIDTH, height: DEFAULT_BOX_HEIGHT },
  box2: { width: DEFAULT_BOX_WIDTH, height: DEFAULT_BOX_HEIGHT },
  box3: { width: DEFAULT_BOX_WIDTH, height: DEFAULT_BOX_HEIGHT },
  box4: { width: DEFAULT_BOX_WIDTH, height: DEFAULT_BOX_HEIGHT },
  box5: { width: DEFAULT_BOX_WIDTH, height: DEFAULT_BOX_HEIGHT },
  box6: { width: DEFAULT_BOX_WIDTH, height: DEFAULT_BOX_HEIGHT },
  box7: { width: DEFAULT_BOX_WIDTH, height: DEFAULT_BOX_HEIGHT },
  box8: { width: DEFAULT_BOX_WIDTH, height: DEFAULT_BOX_HEIGHT },
  box9: { width: DEFAULT_BOX_WIDTH, height: DEFAULT_BOX_HEIGHT },
  box10: { width: DEFAULT_BOX_WIDTH, height: DEFAULT_BOX_HEIGHT },
  box11: { width: DEFAULT_BOX_WIDTH, height: DEFAULT_BOX_HEIGHT },
  box12: { width: DEFAULT_BOX_WIDTH, height: DEFAULT_BOX_HEIGHT }
};

const INITIAL_WIDGETS = {
  box1: null,
  box2: null,
  box3: null,
  box4: null,
  box5: null,
  box6: null,
  box7: null,
  box8: null,
  box9: null,
  box10: null,
  box11: null,
  box12: null
};

const DashboardCreator = ({ 
  onCancel, 
  onDashboardCreated,
  currentUserId,
  dateRange: initialDateRange
}) => {
  const calendarRef = useRef(null);
  const creatorGridRef = useRef(null);
  const { institution } = useInstitution();
  const [maxBoxWidth, setMaxBoxWidth] = useState(800);

  // Estados do criador
  const [creatorWidgets, setCreatorWidgets] = useState(INITIAL_WIDGETS);
  const [availableIndicators, setAvailableIndicators] = useState([]);
  const [loadingIndicators, setLoadingIndicators] = useState(true);
  const [boxSizes, setBoxSizes] = useState(INITIAL_BOX_SIZES);
  const [boxLoading, setBoxLoading] = useState({
    box1: false, box2: false, box3: false,
    box4: false, box5: false, box6: false,
    box7: false, box8: false, box9: false,
    box10: false, box11: false, box12: false
  });
  const [boxPreviewData, setBoxPreviewData] = useState(INITIAL_WIDGETS);
  const [draggingBox, setDraggingBox] = useState(null);

  // Estados de filtros
  const [knowledgeAreas, setKnowledgeAreas] = useState([]);
  const [selectedKnowledgeArea, setSelectedKnowledgeArea] = useState("");
  const [knowledgeAreasLoading, setKnowledgeAreasLoading] = useState(false);
  const [knowledgeAreasFetched, setKnowledgeAreasFetched] = useState(false);

  const [disciplines, setDisciplines] = useState([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState("");
  const [disciplinesLoading, setDisciplinesLoading] = useState(false);
  const [disciplinesFetched, setDisciplinesFetched] = useState(false);

  const [courseTypes, setCourseTypes] = useState([]);
  const [selectedCourseType, setSelectedCourseType] = useState("");
  const [courseTypesLoading, setCourseTypesLoading] = useState(false);
  const [courseTypesFetched, setCourseTypesFetched] = useState(false);

  const [localities, setLocalities] = useState([]);
  const [selectedLocality, setSelectedLocality] = useState("");
  const [localitiesLoading, setLocalitiesLoading] = useState(false);
  const [localitiesFetched, setLocalitiesFetched] = useState(false);

  const [modalities, setModalities] = useState([]);
  const [selectedModality, setSelectedModality] = useState("");
  const [modalitiesLoading, setModalitiesLoading] = useState(false);
  const [modalitiesFetched, setModalitiesFetched] = useState(false);

  // Estados de data
  const [dateRange, setDateRange] = useState(initialDateRange || {
    from: new Date(new Date().getFullYear(), 0, 1),
    to: new Date()
  });
  const [dateRangeString, setDateRangeString] = useState("");
  const [suggestionRange, setSuggestionRange] = useState("");
  const [customMonths, setCustomMonths] = useState("24");
  const [customYears, setCustomYears] = useState("7");
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Estados do modal de criação
  const [savingDashboard, setSavingDashboard] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [dashboardName, setDashboardName] = useState("");
  const [dashboardDescription, setDashboardDescription] = useState("");
  const [visibilityScope, setVisibilityScope] = useState("user");
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useOutsideAlerter(calendarRef, () => setIsCalendarOpen(false));

  // Persistir data no localStorage para compartilhar entre telas
  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      saveDateRange(dateRange);
    }
  }, [dateRange]);

  // Carregar indicadores disponíveis ao montar
  useEffect(() => {
    const fetchIndicators = async () => {
      setLoadingIndicators(true);
      try {
        const indicators = await readAvailableIndicators();
        setAvailableIndicators(indicators || []);
      } catch (error) {
        console.error("Erro ao buscar indicadores:", error);
      } finally {
        setLoadingIndicators(false);
      }
    };
    fetchIndicators();
  }, []);

  useEffect(() => {
    setDateRangeString(
      `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`
    );
  }, [dateRange]);

  useEffect(() => {
    const updateMaxWidth = () => {
      if (creatorGridRef.current) {
        const containerWidth = creatorGridRef.current.offsetWidth;
        setMaxBoxWidth(Math.max(280, containerWidth - 30));
      }
    };

    updateMaxWidth();
    window.addEventListener('resize', updateMaxWidth);
    return () => window.removeEventListener('resize', updateMaxWidth);
  }, []);

  // Funções de filtros
  const fetchKnowledgeAreas = async () => {
    if (knowledgeAreas.length > 0) return;
    try {
      setKnowledgeAreasLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        predictiveFilter: "knowledgearea"
      });
      setKnowledgeAreas(response.filterValues || []);
      setKnowledgeAreasFetched(true);
    } catch (error) {
      console.error("Erro ao buscar áreas de conhecimento:", error);
    } finally {
      setKnowledgeAreasLoading(false);
    }
  };

  const fetchDisciplines = async () => {
    if (disciplines.length > 0) return;
    try {
      setDisciplinesLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        KnowledgeAreaFilter: selectedKnowledgeArea,
        predictiveFilter: "filterclass"
      });
      setDisciplines(response.filterValues || []);
      setDisciplinesFetched(true);
    } catch (error) {
      console.error("Erro ao buscar cursos:", error);
    } finally {
      setDisciplinesLoading(false);
    }
  };

  const fetchCourseTypes = async () => {
    if (courseTypes.length > 0) return;
    try {
      setCourseTypesLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        predictiveFilter: "filterdegree"
      });
      setCourseTypes(response.filterValues || []);
      setCourseTypesFetched(true);
    } catch (error) {
      console.error("Erro ao buscar tipos de curso:", error);
    } finally {
      setCourseTypesLoading(false);
    }
  };

  const fetchLocalities = async () => {
    if (localities.length > 0) return;
    try {
      setLocalitiesLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        predictiveFilter: "filterlocation"
      });
      setLocalities(response.filterValues || []);
      setLocalitiesFetched(true);
    } catch (error) {
      console.error("Erro ao buscar localidades:", error);
    } finally {
      setLocalitiesLoading(false);
    }
  };

  const fetchModalities = async () => {
    if (modalities.length > 0) return;
    try {
      setModalitiesLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        predictiveFilter: "filtermodality"
      });
      setModalities(response.filterValues || []);
      setModalitiesFetched(true);
    } catch (error) {
      console.error("Erro ao buscar modalidades:", error);
    } finally {
      setModalitiesLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (availableUsers.length > 0 || !institution?.id) return;
    
    setLoadingUsers(true);
    try {
      const response = await readByInstitution(institution.id);
      if (response?.users) {
        const filteredUsers = response.users.filter(user => user.id !== currentUserId);
        setAvailableUsers(filteredUsers);
      }
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
      toast.error("Erro ao carregar lista de usuários");
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleKnowledgeAreaChange = (e) => {
    setSelectedKnowledgeArea(e.target.value);
    setSelectedDiscipline("");
    setDisciplines([]);
    setDisciplinesFetched(false);
  };

  const handleDisciplineChange = (e) => {
    setSelectedDiscipline(e.target.value);
  };

  const handleCourseTypeChange = (e) => {
    setSelectedCourseType(e.target.value);
  };

  const handleLocalityChange = (e) => {
    setSelectedLocality(e.target.value);
  };

  const handleModalityChange = (e) => {
    setSelectedModality(e.target.value);
  };

  const selectSuggestionRange = (suggestionRange) => {
    setSuggestionRange(suggestionRange);
    const currentYear = new Date().getFullYear();

    let selectedRange;
    switch (suggestionRange) {
      case "today":
        selectedRange = { from: new Date(), to: new Date() };
        break;
      case "last_7":
        selectedRange = {
          from: new Date(new Date().setDate(new Date().getDate() - 7)),
          to: new Date()
        };
        break;
      case "last_30": {
        const last_30_now = new Date();
        selectedRange = {
          from: new Date(last_30_now.getFullYear(), last_30_now.getMonth(), 1),
          to: new Date(last_30_now.getFullYear(), last_30_now.getMonth() + 1, 0)
        };
        break;
      }
      case "first_semester":
        selectedRange = {
          from: new Date(`${currentYear}-01-01T00:00:00`),
          to: new Date(`${currentYear}-06-30T23:59:59`)
        };
        break;
      case "second_semester":
        selectedRange = {
          from: new Date(`${currentYear}-07-01T00:00:00`),
          to: new Date(`${currentYear}-12-31T23:59:59`)
        };
        break;
      case "last_semester": {
        const today = new Date();
        const isSecondSemester = today.getMonth() >= 6;
        if (isSecondSemester) {
          selectedRange = {
            from: new Date(`${currentYear}-01-01T00:00:00`),
            to: new Date(`${currentYear}-06-30T23:59:59`)
          };
        } else {
          const lastYear = currentYear - 1;
          selectedRange = {
            from: new Date(`${lastYear}-07-01T00:00:00`),
            to: new Date(`${lastYear}-12-31T23:59:59`)
          };
        }
        break;
      }
      case "last_year": {
        const now = new Date();
        selectedRange = {
          from: new Date(now.getFullYear() - 1, now.getMonth(), 1),
          to: new Date(now.getFullYear(), now.getMonth(), 0)
        };
        break;
      }
      case "last_x_months": {
        const monthsBack = parseInt(customMonths, 10);
        if (!isNaN(monthsBack) && monthsBack > 0) {
          const now_lxm = new Date();
          selectedRange = {
            from: new Date(now_lxm.getFullYear(), now_lxm.getMonth() - monthsBack, now_lxm.getDate()),
            to: new Date()
          };
        } else {
          return;
        }
        break;
      }
      case "current_year":
        selectedRange = {
          from: new Date(currentYear, 0, 1),
          to: new Date()
        };
        break;
      case "previous_year": {
        const previousYear = currentYear - 1;
        selectedRange = {
          from: new Date(`${previousYear}-01-01T00:00:00`),
          to: new Date(`${previousYear}-12-31T23:59:59`)
        };
        break;
      }
      case "last_5_years":
        selectedRange = {
          from: new Date(new Date().setFullYear(new Date().getFullYear() - 5)),
          to: new Date()
        };
        break;
      case "last_x_years": {
        const yearsBack = parseInt(customYears, 10);
        if (!isNaN(yearsBack) && yearsBack > 0) {
          const startYear = currentYear - yearsBack;
          selectedRange = {
            from: new Date(`${startYear}-01-01T00:00:00`),
            to: new Date(`${currentYear - 1}-12-31T23:59:59`)
          };
        } else {
          return;
        }
        break;
      }
      default:
        selectedRange = {
          from: new Date(new Date().setMonth(new Date().getMonth() - 3)),
          to: new Date(new Date().setMonth(new Date().getMonth() - 0))
        };
        break;
    }

    setDateRange(selectedRange);
    setDateRangeString(
      `${formatDate(selectedRange.from)} - ${formatDate(selectedRange.to)}`
    );
    setIsCalendarOpen(false);
  };

  const selectManualRange = () => {
    setSuggestionRange("");
    setIsCalendarOpen(false);
  };

  const handleClearDateSelection = () => {
    setSuggestionRange("");
    setDateRange({
      from: new Date(),
      to: new Date()
    });
  };

  // Funções de indicadores
  const handleAddIndicatorToBox = async (boxId, indicator) => {
    setCreatorWidgets((prev) => ({
      ...prev,
      [boxId]: indicator
    }));

    if (shouldOccupyFullRow(indicator)) {
      setBoxSizes((prev) => ({
        ...prev,
        [boxId]: { width: maxBoxWidth, height: prev[boxId]?.height || DEFAULT_BOX_HEIGHT }
      }));
    }

    setBoxLoading((prev) => ({
      ...prev,
      [boxId]: true
    }));

    try {
      const currentYear = new Date().getFullYear();
      const startDate = dateRange?.from 
        ? dateRange.from.toISOString() 
        : new Date(currentYear, 0, 1).toISOString();
      const endDate = dateRange?.to 
        ? dateRange.to.toISOString() 
        : new Date().toISOString();

      const extraParameters = {};
      
      if (selectedCourseType) {
        extraParameters.courseType = selectedCourseType;
      }
      if (selectedKnowledgeArea) {
        extraParameters.knowledgeArea = selectedKnowledgeArea;
      }
      if (selectedDiscipline) {
        extraParameters.course = selectedDiscipline;
      }
      if (selectedLocality) {
        extraParameters.locality = selectedLocality;
      }
      if (selectedModality) {
        extraParameters.modality = selectedModality;
      }

      const params = {
        indicatorCode: indicator.code,
        visualizationType: indicator.visualizationType,
        institutionId: institution?.id,
        startDate,
        endDate,
        extraParameters
      };

      const previewData = await previewIndicator(params);
      const finalPreview = applyMockedValueToPreview(previewData, indicator);

      setBoxPreviewData((prev) => ({
        ...prev,
        [boxId]: finalPreview
      }));
    } catch (error) {
      console.error("Erro ao buscar preview:", error);
      const mockedPreview = applyMockedValueToPreview(null, indicator);
      if (mockedPreview) {
        setBoxPreviewData((prev) => ({
          ...prev,
          [boxId]: mockedPreview
        }));
      }
    } finally {
      setBoxLoading((prev) => ({
        ...prev,
        [boxId]: false
      }));
    }
  };

  const handleRemoveCreatorWidget = (boxId) => {
    setCreatorWidgets((prev) => ({
      ...prev,
      [boxId]: null
    }));
    setBoxPreviewData((prev) => ({
      ...prev,
      [boxId]: null
    }));
  };

  const handleResize = (boxId, size) => {
    setBoxSizes((prev) => ({
      ...prev,
      [boxId]: { width: size.width, height: size.height }
    }));
  };

  // Funções de drag and drop
  const handleBoxDragStart = (boxId) => {
    setDraggingBox(boxId);
  };

  const handleBoxDragEnd = () => {
    setDraggingBox(null);
  };

  const handleBoxDrop = (targetBoxId) => {
    if (!draggingBox || draggingBox === targetBoxId) return;

    const sourceWidget = creatorWidgets[draggingBox];
    const targetWidget = creatorWidgets[targetBoxId];
    const sourcePreview = boxPreviewData[draggingBox];
    const targetPreview = boxPreviewData[targetBoxId];
    const sourceSize = boxSizes[draggingBox];
    const targetSize = boxSizes[targetBoxId];

    setCreatorWidgets((prev) => ({
      ...prev,
      [draggingBox]: targetWidget,
      [targetBoxId]: sourceWidget
    }));

    setBoxPreviewData((prev) => ({
      ...prev,
      [draggingBox]: targetPreview,
      [targetBoxId]: sourcePreview
    }));

    setBoxSizes((prev) => ({
      ...prev,
      [draggingBox]: targetSize,
      [targetBoxId]: sourceSize
    }));

    setDraggingBox(null);
  };

  // Funções do modal de criação
  const handleOpenCreateModal = () => {
    setDashboardName("");
    setDashboardDescription("");
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setDashboardName("");
    setDashboardDescription("");
    setVisibilityScope("user");
    setSelectedUsers([]);
  };

  const handleVisibilityScopeChange = (scope) => {
    setVisibilityScope(scope);
    if (scope === 'specific') {
      fetchUsers();
    } else {
      setSelectedUsers([]);
    }
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const getBoxPosition = (boxId) => {
    const boxNumber = parseInt(boxId.replace('box', '')) - 1;
    const row = Math.floor(boxNumber / GRID_COLUMNS);
    const col = boxNumber % GRID_COLUMNS;
    return { row, col };
  };

  const handleCreateDashboard = async () => {
    if (!currentUserId || !institution?.id) {
      toast.error("Erro: Usuário ou instituição não encontrados");
      return;
    }

    if (!dashboardName.trim()) {
      toast.error("Por favor, preencha o nome do dashboard");
      return;
    }

    const widgets = Object.entries(creatorWidgets)
      .filter(([_, widget]) => widget !== null)
      .map(([boxId, widget]) => {
        const { row, col } = getBoxPosition(boxId);
        const size = boxSizes[boxId];
        return {
          indicatorCode: widget.code,
          visualizationType: widget.visualizationType,
          row,
          col,
          width: Math.round(size.width / DEFAULT_BOX_WIDTH),
          height: Math.round(size.height / DEFAULT_BOX_HEIGHT)
        };
      });

    if (widgets.length === 0) {
      toast.error("Adicione pelo menos um indicador ao dashboard");
      return;
    }

    setSavingDashboard(true);

    try {
      const currentYear = new Date().getFullYear();
      const startDate = dateRange?.from 
        ? dateRange.from.toISOString() 
        : new Date(currentYear, 0, 1).toISOString();
      const endDate = dateRange?.to 
        ? dateRange.to.toISOString() 
        : new Date().toISOString();

      const globalFilters = {
        startDate,
        endDate
      };

      if (selectedCourseType) {
        globalFilters.courseType = selectedCourseType;
      }
      if (selectedKnowledgeArea) {
        globalFilters.knowledgeArea = selectedKnowledgeArea;
      }
      if (selectedDiscipline) {
        globalFilters.course = selectedDiscipline;
      }
      if (selectedLocality) {
        globalFilters.locality = selectedLocality;
      }
      if (selectedModality) {
        globalFilters.modality = selectedModality;
      }

      const payload = {
        name: dashboardName.trim(),
        description: dashboardDescription.trim() || "Dashboard personalizado",
        visibilityScope: visibilityScope === 'specific' ? 'institution' : visibilityScope,
        ownerUserId: currentUserId,
        userId: currentUserId,
        globalFilters,
        widgets,
        ...(visibilityScope === 'specific' && selectedUsers.length > 0 && {
          allowedUserIds: selectedUsers
        })
      };

      const createdDashboard = await createCustomizerDashboard(payload, institution.id);
      toast.success("Dashboard criado com sucesso!");
      setShowCreateModal(false);

      if (createdDashboard?.dashboardId && onDashboardCreated) {
        try {
          const startDateStr = formatDate(dateRange.from).replaceAll("/", "-");
          const endDateStr = formatDate(dateRange.to).replaceAll("/", "-");
          
          const data = await getDashboardData(
            createdDashboard.dashboardId, 
            currentUserId, 
            institution.id,
            startDateStr,
            endDateStr
          );
          onDashboardCreated(createdDashboard, data);
        } catch (err) {
          console.error("Erro ao buscar dados do dashboard:", err);
          onDashboardCreated(createdDashboard, null);
        }
      } else if (onDashboardCreated) {
        onDashboardCreated(createdDashboard, null);
      }
    } catch (error) {
      console.error("Erro ao criar dashboard:", error);
      toast.error("Erro ao criar dashboard. Tente novamente.");
    } finally {
      setSavingDashboard(false);
    }
  };

  const renderCreatorBox = (boxId) => {
    const widget = creatorWidgets[boxId];
    const size = boxSizes[boxId];
    const isLoading = boxLoading[boxId];
    const previewData = boxPreviewData[boxId];
    const isDragging = draggingBox === boxId;
    const isDropTarget = draggingBox && draggingBox !== boxId;

    if (widget) {
      return (
        <Resizable
          width={size.width}
          height={size.height}
          onResize={(e, { size }) => handleResize(boxId, size)}
          minConstraints={[280, 200]}
          maxConstraints={[maxBoxWidth, 600]}
          handle={<div className={styles.resizeHandle} />}
        >
          <div 
            className={clsx(
              styles.creatorBox, 
              styles.hasIndicator,
              isDragging && styles.isDragging,
              isDropTarget && styles.isDropTarget
            )}
            style={{ width: size.width, height: size.height }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleBoxDrop(boxId);
            }}
          >
            <button
              className={styles.removeCreatorBtn}
              onClick={() => handleRemoveCreatorWidget(boxId)}
            >
              ×
            </button>
            <div 
              className={styles.creatorBoxDragArea}
              draggable
              onDragStart={(e) => {
                e.stopPropagation();
                handleBoxDragStart(boxId);
              }}
              onDragEnd={(e) => {
                e.stopPropagation();
                handleBoxDragEnd();
              }}
            >
              <div className={styles.dragHandle}>⋮⋮</div>
            </div>
            <div className={styles.creatorBoxContent}>
              {isLoading ? (
                <div className={styles.creatorBoxLoading}>
                  {widget.icon && (
                    <img 
                      src={getIconByName(widget.icon)} 
                      alt={widget.name}
                      className={styles.creatorBoxLoadingIcon}
                    />
                  )}
                  <span className={styles.creatorBoxLoadingName}>{widget.name}</span>
                  <span className={styles.creatorBoxLoadingText}>Carregando dados...</span>
                </div>
              ) : previewData ? (
                <div className={styles.creatorBoxPreview}>
                  {previewData.visualizationType === "label" && (
                    <div className={styles.labelCard}>
                      {previewData.icon && (
                        <img 
                          src={getIconByName(previewData.icon)} 
                          alt={previewData.name}
                          className={styles.labelCardIcon}
                        />
                      )}
                      <div className={styles.labelCardHeader}>
                        <h6 className={styles.labelCardTitle}>{previewData.name}</h6>
                        <h2 className={styles.labelCardValue}>
                          {formatValueByType(previewData.data?.value || 0, previewData.labelType)}
                        </h2>
                      </div>
                    </div>
                  )}
                  {previewData.visualizationType === "time_series" && previewData.data?.points && (
                    <div className={styles.chartWidgetContainer}>
                      <div className={styles.chartWidgetHeader}>
                        {previewData.icon && (
                          <img 
                            src={getIconByName(previewData.icon)} 
                            alt={previewData.name}
                            className={styles.chartWidgetIcon}
                          />
                        )}
                        <h4 className={styles.chartWidgetTitle}>{previewData.name}</h4>
                      </div>
                      <div className={styles.chartContainer}>
                        <Line
                          data={{
                            labels: previewData.data.points.map((point) => point.x),
                            datasets: [
                              {
                                label: previewData.name,
                                data: previewData.data.points.map((point) => point.y),
                                borderColor: "#0962AD",
                                backgroundColor: "rgba(9, 98, 173, 0.1)",
                                fill: true,
                                tension: 0.4,
                                pointRadius: 4,
                                pointBackgroundColor: "#0962AD"
                              }
                            ]
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                display: false
                              },
                              tooltip: {
                                callbacks: {
                                  label: (context) => {
                                    return formatValueByType(context.raw, previewData.labelType);
                                  }
                                }
                              }
                            },
                            scales: {
                              x: {
                                grid: {
                                  display: false
                                },
                                ticks: {
                                  font: {
                                    size: 10
                                  }
                                }
                              },
                              y: {
                                grid: {
                                  color: "rgba(0, 0, 0, 0.05)"
                                },
                                ticks: {
                                  font: {
                                    size: 10
                                  },
                                  callback: (value) => {
                                    return formatValueByType(value, previewData.labelType, { notation: 'compact' });
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.creatorBoxInfo}>
                  {widget.icon && (
                    <img 
                      src={getIconByName(widget.icon)} 
                      alt={widget.name}
                      className={styles.creatorBoxIcon}
                    />
                  )}
                  <span className={styles.creatorBoxCategory}>{widget.category}</span>
                  <span className={styles.creatorBoxName}>{widget.name}</span>
                  <span className={styles.creatorBoxDescription}>{widget.description}</span>
                </div>
              )}
            </div>
          </div>
        </Resizable>
      );
    }

    return (
      <div
        className={clsx(
          styles.creatorBox,
          isDropTarget && styles.isDropTarget
        )}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (draggingBox) {
            handleBoxDrop(boxId);
          } else {
            const indicatorData = e.dataTransfer.getData("indicator");
            if (indicatorData) {
              const indicator = JSON.parse(indicatorData);
              handleAddIndicatorToBox(boxId, indicator);
            }
          }
        }}
      >
        <span className={styles.creatorBoxPlaceholder}>Arraste um indicador aqui</span>
      </div>
    );
  };

  const widgetCount = Object.values(creatorWidgets).filter(w => w !== null).length;

  return (
    <div className={styles.customDashboardWrapper}>
      <HeaderTitle title="Criar Dashboard Customizado" icon={DashboardIcon}>
        <Button
          icon={ArrowLeftIcon}
          text="Voltar"
          size="normal"
          variation="outline"
          onClick={onCancel}
        />
      </HeaderTitle>
      
      <div className={styles.creatorFiltersWrapper}>
        <div className={styles.creatorFiltersContent}>
          <FilterSelect
            icon={CourseIcon}
            label="Tipo de Curso"
            value={selectedCourseType}
            loading={courseTypesLoading}
            options={courseTypes}
            onFocus={fetchCourseTypes}
            hasFetched={courseTypesFetched}
            onChange={handleCourseTypeChange}
          />
          <FilterSelect
            icon={BookIcon}
            label="Área de Conhecimento"
            value={selectedKnowledgeArea}
            loading={knowledgeAreasLoading}
            options={knowledgeAreas}
            onFocus={fetchKnowledgeAreas}
            hasFetched={knowledgeAreasFetched}
            onChange={handleKnowledgeAreaChange}
          />
          <FilterSelect
            icon={DisciplineIcon}
            label="Curso"
            value={selectedDiscipline}
            loading={disciplinesLoading}
            options={disciplines}
            onFocus={fetchDisciplines}
            hasFetched={disciplinesFetched}
            onChange={handleDisciplineChange}
          />
          <FilterSelect
            icon={LocaleIcon}
            label="Localidade"
            value={selectedLocality}
            loading={localitiesLoading}
            options={localities}
            onFocus={fetchLocalities}
            hasFetched={localitiesFetched}
            onChange={handleLocalityChange}
          />
          <FilterSelect
            icon={ClassesIcon}
            label="Modalidade"
            value={selectedModality}
            loading={modalitiesLoading}
            options={modalities}
            onFocus={fetchModalities}
            hasFetched={modalitiesFetched}
            onChange={handleModalityChange}
          />
        </div>
        
        {/* Resumo dos filtros ativos */}
        {(selectedCourseType || selectedKnowledgeArea || selectedDiscipline || selectedLocality || selectedModality) && (
          <div className={styles.activeFiltersInfo}>
            <span className={styles.activeFiltersLabel}>Filtros que serão salvos:</span>
            <div className={styles.activeFiltersList}>
              {selectedCourseType && (
                <span className={styles.activeFilterBadge}>
                  Tipo: {courseTypes.find(ct => ct.value === selectedCourseType)?.label || selectedCourseType}
                </span>
              )}
              {selectedKnowledgeArea && (
                <span className={styles.activeFilterBadge}>
                  Área: {knowledgeAreas.find(ka => ka.value === selectedKnowledgeArea)?.label || selectedKnowledgeArea}
                </span>
              )}
              {selectedDiscipline && (
                <span className={styles.activeFilterBadge}>
                  Curso: {disciplines.find(d => d.value === selectedDiscipline)?.label || selectedDiscipline}
                </span>
              )}
              {selectedLocality && (
                <span className={styles.activeFilterBadge}>
                  Localidade: {localities.find(l => l.value === selectedLocality)?.label || selectedLocality}
                </span>
              )}
              {selectedModality && (
                <span className={styles.activeFilterBadge}>
                  Modalidade: {modalities.find(m => m.value === selectedModality)?.label || selectedModality}
                </span>
              )}
            </div>
          </div>
        )}
        
        <div className={styles.creatorFiltersActions}>
          <div className={styles.filterButtonWrapper}>
            <Button
              icon={CalendarIcon}
              text={dateRangeString}
              size="normal"
              variation="outline"
              onClick={() => setIsCalendarOpen(!isCalendarOpen)}
            />

            {isCalendarOpen && (
              <div ref={calendarRef} className={styles.calendarDropdown}>
                <div className={styles.suggestions}>
                  <ul>
                    <li
                      onClick={() => selectSuggestionRange("today")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "today"
                      })}
                    >
                      Hoje
                    </li>
                    <li
                      onClick={() => selectSuggestionRange("last_7")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_7"
                      })}
                    >
                      Últimos 7 dias
                    </li>
                    <li
                      onClick={() => selectSuggestionRange("last_30")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_30"
                      })}
                    >
                      Último mês
                    </li>
                    <li
                      onClick={() => selectSuggestionRange("first_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "first_semester"
                      })}
                    >
                      Primeiro Semestre
                    </li>
                    <li
                      onClick={() => selectSuggestionRange("second_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "second_semester"
                      })}
                    >
                      Segundo Semestre
                    </li>
                    <li
                      onClick={() => selectSuggestionRange("last_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_semester"
                      })}
                    >
                      Último Semestre
                    </li>
                    <li
                      onClick={() => selectSuggestionRange("last_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_year"
                      })}
                    >
                      Últimos 12 meses
                    </li>
                    <li
                      className={clsx(styles.suggestionsItem, styles.customMonthsItem, {
                        [styles.active]: suggestionRange === "last_x_months"
                      })}
                    >
                      <span>Últimos</span>
                      <input
                        type="number"
                        min="1"
                        className={styles.customMonthsInput}
                        value={customMonths}
                        placeholder="24"
                        onChange={(e) => setCustomMonths(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && customMonths) {
                            selectSuggestionRange("last_x_months");
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span
                        style={{ cursor: !customMonths ? 'default' : 'pointer' }}
                        onClick={() => {
                          if (customMonths) {
                            selectSuggestionRange("last_x_months");
                          }
                        }}
                      >
                        meses
                      </span>
                    </li>
                    <li
                      onClick={() => selectSuggestionRange("current_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "current_year"
                      })}
                    >
                      Ano Atual
                    </li>
                    <li
                      onClick={() => selectSuggestionRange("previous_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "previous_year"
                      })}
                    >
                      Ano Anterior
                    </li>
                    <li
                      onClick={() => selectSuggestionRange("last_5_years")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_5_years"
                      })}
                    >
                      Últimos 5 Anos
                    </li>
                    <li
                      className={clsx(styles.suggestionsItem, styles.customMonthsItem, {
                        [styles.active]: suggestionRange === "last_x_years"
                      })}
                    >
                      <span>Últimos</span>
                      <input
                        type="number"
                        min="1"
                        className={styles.customMonthsInput}
                        value={customYears}
                        placeholder="7"
                        onChange={(e) => setCustomYears(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && customYears) {
                            selectSuggestionRange("last_x_years");
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span
                        style={{ cursor: !customYears ? 'default' : 'pointer' }}
                        onClick={() => {
                          if (customYears) {
                            selectSuggestionRange("last_x_years");
                          }
                        }}
                      >
                        anos
                      </span>
                    </li>
                    <li
                      onClick={() => selectSuggestionRange("manual")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "manual"
                      })}
                    >
                      Personalizado
                    </li>
                  </ul>
                  <div>
                    <div
                      onClick={handleClearDateSelection}
                      className={clsx(styles.suggestionsItem, styles.active)}
                    >
                      Limpar seleção
                    </div>
                    <div
                      onClick={() => selectManualRange()}
                      className={clsx(
                        styles.suggestionsItem,
                        styles.active,
                        styles["btn-search-data"]
                      )}
                    >
                      <img src={MagnifyingGlassIcon} alt="magnifying-glass" />
                      Buscar dados
                    </div>
                  </div>
                </div>
                <div className={styles.dateRangeGroup}>
                  <div className={styles.datePickerWrapper}>
                    <label>Data de Início</label>
                    <DayPicker
                      mode="single"
                      selected={dateRange?.from}
                      defaultMonth={dateRange?.from}
                      onSelect={(from) => setDateRange((prev) => ({ ...prev, from }))}
                      locale={ptBR}
                      captionLayout="dropdown"
                    />
                  </div>
                  <div className={styles.datePickerWrapper}>
                    <label>Data de Fim</label>
                    <DayPicker
                      mode="single"
                      selected={dateRange?.to}
                      defaultMonth={dateRange?.to}
                      onSelect={(to) => setDateRange((prev) => ({ ...prev, to }))}
                      locale={ptBR}
                      captionLayout="dropdown"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.creatorContainer}>
        <div className={styles.creatorGridArea}>
          <div ref={creatorGridRef} className={styles.creatorGrid}>
            {renderCreatorBox("box1")}
            {renderCreatorBox("box2")}
            {renderCreatorBox("box3")}
            {renderCreatorBox("box4")}
            {renderCreatorBox("box5")}
            {renderCreatorBox("box6")}
            {renderCreatorBox("box7")}
            {renderCreatorBox("box8")}
            {renderCreatorBox("box9")}
            {renderCreatorBox("box10")}
            {renderCreatorBox("box11")}
            {renderCreatorBox("box12")}
          </div>
        </div>
        <div className={styles.indicatorsSidebar}>
          <h3 className={styles.sidebarTitle}>Indicadores</h3>
          <div className={styles.indicatorsList}>
            {loadingIndicators ? (
              <p className={styles.loadingText}>Carregando indicadores...</p>
            ) : availableIndicators.length === 0 ? (
              <p className={styles.emptyText}>Nenhum indicador disponível</p>
            ) : (
              <>
                {["Financeiro", "Educacional"].map((category) => {
                  const categoryIndicators = availableIndicators.filter(
                    (ind) => ind.category === category
                  );
                  if (categoryIndicators.length === 0) return null;
                  return (
                    <div key={category} className={styles.indicatorCategory}>
                      <h4 className={styles.categoryTitle}>{category}</h4>
                      {categoryIndicators.map((indicator) => (
                        <div
                          key={indicator.indicatorId}
                          className={styles.indicatorItem}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData("indicator", JSON.stringify(indicator));
                          }}
                        >
                          {indicator.icon && (
                            <img 
                              src={getIconByName(indicator.icon)} 
                              alt={indicator.name}
                              className={styles.indicatorIcon}
                            />
                          )}
                          <div className={styles.indicatorInfo}>
                            <span className={styles.indicatorName}>{indicator.name}</span>
                            <span className={styles.indicatorDescription}>{indicator.description}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>

      <div className={styles.createButtonWrapper}>
        <div className={styles.footerInfo}>
          <span className={styles.footerText}>
            {widgetCount === 0 
              ? "Arraste indicadores para os espaços vazios para montar seu dashboard"
              : `Você adicionou ${widgetCount} ${widgetCount === 1 ? 'indicador' : 'indicadores'} a este dashboard.`
            }
          </span>
        </div>
        <div className={styles.footerActions}>
          <Button
            text="Cancelar"
            size="normal"
            variation="outline"
            onClick={onCancel}
          />
          <Button
            text="Criar Dashboard"
            size="normal"
            variation="primary"
            onClick={handleOpenCreateModal}
            disabled={widgetCount === 0}
          />
        </div>
      </div>

      {showCreateModal && (
        <div className={styles.modalBackdrop} onClick={handleCloseCreateModal}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeaderNew}>
              <h3>Criar Dashboard</h3>
              <button className={styles.closeButtonNew} onClick={handleCloseCreateModal}>
                ×
              </button>
            </div>

            <p className={styles.modalDescription}>
              Configure o seu dashboard personalizado.
            </p>

            <div className={styles.formGroup}>
              <label htmlFor="dashboard-name">Nome do Dashboard *</label>
              <input
                id="dashboard-name"
                type="text"
                className={styles.input}
                placeholder="Ex: Meu Dashboard Financeiro"
                value={dashboardName}
                onChange={(e) => setDashboardName(e.target.value)}
                maxLength={100}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="dashboard-description">Descrição</label>
              <textarea
                id="dashboard-description"
                className={styles.textarea}
                placeholder="Descreva seu dashboard..."
                value={dashboardDescription}
                onChange={(e) => setDashboardDescription(e.target.value)}
                rows={3}
                maxLength={500}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Visibilidade</label>
              <div className={styles.visibilityCards}>
                <div
                  className={`${styles.visibilityCard} ${visibilityScope === 'user' ? styles.visibilityCardActive : ''}`}
                  onClick={() => handleVisibilityScopeChange('user')}
                >
                  <div className={styles.visibilityCardIcon}>
                    <img src={UserIcon} alt="Apenas eu" />
                  </div>
                  <div className={styles.visibilityCardContent}>
                    <span className={styles.visibilityCardLabel}>Apenas eu</span>
                    <span className={styles.visibilityCardDescription}>Visível somente para você</span>
                  </div>
                </div>
                <div
                  className={`${styles.visibilityCard} ${visibilityScope === 'institution' ? styles.visibilityCardActive : ''}`}
                  onClick={() => handleVisibilityScopeChange('institution')}
                >
                  <div className={styles.visibilityCardIcon}>
                    <img src={UsersIcon} alt="Toda instituição" />
                  </div>
                  <div className={styles.visibilityCardContent}>
                    <span className={styles.visibilityCardLabel}>Toda instituição</span>
                    <span className={styles.visibilityCardDescription}>Visível para todos</span>
                  </div>
                </div>
                <div
                  className={`${styles.visibilityCard} ${visibilityScope === 'specific' ? styles.visibilityCardActive : ''}`}
                  onClick={() => handleVisibilityScopeChange('specific')}
                >
                  <div className={styles.visibilityCardIcon}>
                    <img src={UsersTwoIcon} alt="Usuários específicos" />
                  </div>
                  <div className={styles.visibilityCardContent}>
                    <span className={styles.visibilityCardLabel}>Usuários específicos</span>
                    <span className={styles.visibilityCardDescription}>Escolha quem pode ver</span>
                  </div>
                </div>
              </div>

              {visibilityScope === 'specific' && (
                <div className={styles.usersList}>
                  <div className={styles.usersListHeader}>
                    <span className={styles.usersListTitle}>Selecione os usuários</span>
                    {selectedUsers.length > 0 && (
                      <span className={styles.usersListCount}>
                        {selectedUsers.length} selecionado{selectedUsers.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  {loadingUsers ? (
                    <div className={styles.usersListLoading}>
                      Carregando usuários...
                    </div>
                  ) : availableUsers.length === 0 ? (
                    <div className={styles.usersListEmpty}>
                      Nenhum usuário disponível
                    </div>
                  ) : (
                    <div className={styles.usersListItems}>
                      {(() => {
                        const roleLabels = {
                          'Admin': 'Administradores',
                          'Manager': 'Gerentes',
                          'Financial': 'Financeiro',
                          'Operator': 'Operadores'
                        };

                        const groupedUsers = availableUsers.reduce((groups, user) => {
                          const role = user.role || 'User';
                          if (!groups[role]) {
                            groups[role] = [];
                          }
                          groups[role].push(user);
                          return groups;
                        }, {});

                        const roleOrder = ['Admin', 'Manager', 'Financial', 'User', 'Viewer'];
                        const sortedRoles = Object.keys(groupedUsers).sort((a, b) => {
                          const indexA = roleOrder.indexOf(a);
                          const indexB = roleOrder.indexOf(b);
                          if (indexA === -1 && indexB === -1) return a.localeCompare(b);
                          if (indexA === -1) return 1;
                          if (indexB === -1) return -1;
                          return indexA - indexB;
                        });

                        return sortedRoles.map(role => (
                          <div key={role} className={styles.roleGroup}>
                            <div className={styles.roleHeader}>
                              <span className={styles.roleLabel}>{roleLabels[role] || role}</span>
                              <span className={styles.roleCount}>{groupedUsers[role].length}</span>
                            </div>
                            {groupedUsers[role].map(user => (
                              <label 
                                key={user.id} 
                                className={`${styles.userItem} ${selectedUsers.includes(user.id) ? styles.userItemSelected : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedUsers.includes(user.id)}
                                  onChange={() => handleUserToggle(user.id)}
                                  className={styles.userCheckbox}
                                />
                                <span className={styles.userName}>{user.firstName} {user.lastName}</span>
                                <span className={styles.userEmailSeparator}>•</span>
                                <span className={styles.userEmail}>{user.email}</span>
                              </label>
                            ))}
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className={styles.modalActionsNew}>
              <Button
                text="Cancelar"
                variation="secondary"
                size="medium"
                onClick={handleCloseCreateModal}
                styling={{
                  padding: "8px 20px",
                  fontSize: "13px"
                }}
              />
              <Button
                text={savingDashboard ? "Criando..." : "Criar"}
                variation="primary"
                size="medium"
                onClick={handleCreateDashboard}
                disabled={savingDashboard || !dashboardName.trim()}
                styling={{
                  padding: "8px 20px",
                  fontSize: "13px"
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardCreator;

